import React, { FC, ReactElement, useEffect, useRef } from 'react';
import { Theme } from '@mui/material/styles';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { clsx } from 'clsx';
// Components
import Text from '../text';
import SvgIcon from '../icons/SvgIcon';
import BackButtonBase from '../buttons/backButtonBase';
import DrawerMenuResponsive from './drawerMenuResponsive';
import DrawerMenuResponsiveV2 from './drawerMenuResponsiveV2';
import Space from '../layout/space';
import { IconAxel, IconCommon, IconLogo, ImageEvent } from '../../resources/images';
import menuSideBase64 from '../../resources/images/base64/menuSide';
import menuSideCloseBase64 from '../../resources/images/base64/menuSideClose';
// Resources
import { paths } from '../../navigation/routes/paths';
import packageJson from '../../../package.json';
import { stringsRes } from '../../resources/strings';
import { isUserAdmin } from '../../utils/user';
import {
    KEY_LOCAL_STORAGE_NATIVE_OFFLINE,
    KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE,
    KEY_WV_MOBILE_VERSION,
    asyncStorageApi,
    getBottomBarHeight,
    getTopBarHeight,
} from '../../localStorageAPI';
import { getLastPathname } from '../../utils/strings';
import { isDeviceIOS, isWebView } from '../../utils/webview';
import { isOnFlow, noTopBar } from '../../utils/page';
import { navigateTo } from '../../utils/redirect';
import { DEFAULT_TOP_BAR_HEIGHT } from '../../utils/constants';
import WickedImage from '../events/wicked/WickedImage';
import { useEvent } from '../../hooks/useEvent';
import { EventType } from '../../core/apiTypes';

// Change the menu considering this number
const MENU: number = 1;

interface StyleProps {
    statusBarHeight: number;
    bottomBarHeight: number;
    isWebview: boolean;
}

interface MobileAppBarProps {
    isDrawerOpen: boolean;
    onOpenDrawer: () => void;
    onCloseDrawer: () => void;
    onLogout: () => void;
    shouldRefresh?: boolean;
}

const MobileAppBar: FC<MobileAppBarProps> = (props): ReactElement => {
    const { isDrawerOpen, onOpenDrawer, onCloseDrawer, onLogout, shouldRefresh = false } = props;
    const nextLocation = useRef(null);
    const isWebview = isWebView();
    const isIOS = isDeviceIOS();
    const classes = useStyles({
        isWebview,
        statusBarHeight: getTopBarHeight() || DEFAULT_TOP_BAR_HEIGHT,
        bottomBarHeight: getBottomBarHeight(),
    });
    const navigate = useNavigate();
    const location = useLocation();
    const { currentEvent } = useEvent();

    //= ========================================//
    //= ============== UseEffect ===============//
    //= ========================================//

    useEffect(() => {
        // We show the offline banner again when we change page
        if (isWebview && asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE)) {
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE, true);
            window.dispatchEvent(new Event('storage'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    useEffect(() => {
        if (nextLocation.current && !isDrawerOpen) {
            navigateTo(navigate, nextLocation.current);
            nextLocation.current = null;
            if (shouldRefresh) {
                navigate(0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDrawerOpen]);

    //= ======================================//
    //= ============== Actions ===============//
    //= ======================================//

    // const onClearCache = () => {
    //     console.log('Asking app to remove cache');
    //     sendMessageToNativeApp(WebViewChannel.RESET_CACHE, '');
    // };

    // MUI focuses the drawer paper on open and keeps the drawer mounted, so it flags the closed drawer aria-hidden.
    // It can only restore focus to the burger button while that button is mounted, and it unmounts as soon as we
    // leave the home page. We drop the focus ourselves so no focused node is ever left inside the hidden drawer.
    const handleCloseDrawer = () => {
        const focused = document.activeElement as HTMLElement | null;
        if (focused?.closest('.MuiDrawer-root')) {
            focused.blur();
        }
        onCloseDrawer();
    };

    const handleClickOnPath = (newLocation: string) => {
        nextLocation.current = newLocation;
        handleCloseDrawer();
    };

    //= =====================================//
    //= ============== Render ===============//
    //= =====================================//

    const { pathname } = window.location;
    const onFlow = isOnFlow();
    const onNoTopBar = noTopBar();
    const onHomePage = pathname === paths.home;
    const isWicked = currentEvent === EventType.WICKED;

    // Versions
    const appVersion = packageJson.version;
    const mobileVersion = asyncStorageApi.restoreDataNoLink(KEY_WV_MOBILE_VERSION);

    return (
        <>
            {/* {onFlow && (
                <RouterLink
                    to={paths.home}
                    onClick={() => {
                        navigate(paths.home);
                        if (shouldRefresh) {
                            navigate(0);
                        }
                        nextLocation.current = null;
                    }}
                    className={classes.absoluteLogo}
                >
                    <Tooltip title={`V ${appVersion}`}>
                        <span>
                            <SvgIcon icon={IconLogo.Logo} size="inherit" stroke="none" />
                        </span>
                    </Tooltip>
                </RouterLink>
            )} */}
            {!onNoTopBar && (
                <div style={{ paddingLeft: 30, paddingTop: 13 }} onClick={() => {}}>
                    <BackButtonBase
                        onClickAction={() => {
                            if (pathname.split('/')?.length > 2 || getLastPathname(pathname) === 'ride_tracking') {
                                navigate(-1);
                            } else {
                                navigate(paths.home);
                                onOpenDrawer();
                            }
                        }}
                    />
                </div>
            )}
            {MENU !== 2 && (onHomePage || isDrawerOpen) && (
                <IconButton
                    {...{
                        edge: 'start',
                        color: 'primary',
                        'aria-label': 'menu',
                        'aria-haspopup': 'true',
                        onClick: onOpenDrawer,
                        className: clsx(
                            classes.menuPadding,
                            !isDrawerOpen && classes.leftBurger,
                            isWebview && classes.statusBarTop,
                        ),
                    }}
                    size="medium"
                >
                    <SvgIcon icon={IconCommon.MenuBurger} stroke="currentColor" />
                </IconButton>
            )}
            {MENU === 2 && onHomePage && (
                <img
                    alt="menuOpen"
                    onClick={(e: any) => {
                        e.stopPropagation();
                        onOpenDrawer();
                    }}
                    className={clsx(classes.imgNotOpen, isWebview && classes.statusBarTop)}
                    src={menuSideBase64}
                />
            )}
            {onFlow && (
                <SwipeableDrawer
                    classes={{ paper: clsx(classes.muiDrawer, MENU === 2 ? classes.menuSide : classes.menu) }}
                    {...{
                        anchor: 'left',
                        open: isDrawerOpen,
                        onOpen: onOpenDrawer,
                        onClose: handleCloseDrawer,
                    }}
                    disableBackdropTransition={!isIOS}
                    disableDiscovery={isIOS}
                >
                    <div className={clsx(classes.paddingAppBar, isWebview && classes.statusBar)}>
                        <div className={classes.toolbar}>
                            <RouterLink
                                to={paths.home}
                                className={classes.noDecoration}
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    navigate(paths.home);
                                    if (shouldRefresh) {
                                        navigate(0);
                                    }
                                    nextLocation.current = null;
                                    handleCloseDrawer();
                                }}
                            >
                                {MENU === 2 && <Space value={5} />}
                                {/* <Tooltip title={`V ${appVersion}`}> */}
                                <span>
                                    <SvgIcon
                                        icon={IconLogo.Logo}
                                        size="inherit"
                                        stroke="none"
                                        className={MENU === 2 ? classes.whiteLogo : classes.bleuLogo}
                                    />
                                </span>
                                {/* </Tooltip> */}
                            </RouterLink>
                            {MENU !== 2 && (
                                <div>
                                    <IconButton
                                        {...{
                                            edge: 'start',
                                            color: 'primary',
                                            'aria-label': 'menu',
                                            'aria-haspopup': 'true',
                                            onClick: (e: any) => {
                                                e.stopPropagation();
                                                handleCloseDrawer();
                                            },
                                        }}
                                        size="medium"
                                    >
                                        <SvgIcon
                                            icon={IconAxel.ArrowLeft}
                                            overwritePathStroke
                                            color="darkGrey"
                                            fill="none"
                                        />
                                    </IconButton>
                                </div>
                            )}
                        </div>
                    </div>
                    {MENU === 2 && (
                        <div className={classes.imgContainer}>
                            <img
                                alt="menuClose"
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    handleCloseDrawer();
                                }}
                                className={classes.img}
                                src={menuSideCloseBase64}
                            />
                        </div>
                    )}
                    {MENU !== 2 ? (
                        <DrawerMenuResponsive noAdmin={!isUserAdmin()} onClick={handleClickOnPath} />
                    ) : (
                        <DrawerMenuResponsiveV2 noAdmin={!isUserAdmin()} onClick={handleClickOnPath} />
                    )}

                    {MENU === 2 && (
                        <div>
                            {/* UNCOMMENT FOR - Clean Cache  
                        {!configs.envConfig.currentEnv.isProduction && isWebview && (
                            <MenuItem onClick={onClearCache} className={classes.menuItem}>
                                <Text.ShortMediumText16 customColor="white" text={'ClearCache'} />
                            </MenuItem>
                        )} */}
                            <MenuItem onClick={onLogout} className={classes.menuItem}>
                                <Text.ShortMediumText16 customColor="white" text={stringsRes.profile.logout} />
                            </MenuItem>
                        </div>
                    )}
                    {isWicked && (
                        <>
                            <div className={classes.spacer} />
                            <WickedImage type={ImageEvent.WickedMenu} />
                        </>
                    )}
                    <div className={classes.spacer} />
                    <div className={clsx(classes.versions, 'version')}>{`${
                        mobileVersion ? `${mobileVersion} - ${appVersion}` : appVersion
                    }`}</div>
                </SwipeableDrawer>
            )}
        </>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        menuSide: {},
        menu: {
            overflow: 'auto!important',
            backgroundColor: `${theme.palette.colors.background.appBar}!important`,
            '& .version': {
                color: theme.palette.primary.main,
            },
        },
        menuItem: {
            // padding: '20px 20px',
            padding: '24px 30px',
            '& .MuiListItemIcon-root': {
                minWidth: 'inherit',
            },
            '&:hover': {
                '& .MuiListItemIcon-root': {
                    color: 'unset',
                },
                color: theme.palette.colors.text.accent,
            },
        },
        absoluteLogo: {
            position: 'absolute',
            top: 15,
            left: 20,
        },
        spacer: { display: 'flex', flex: 1, minHeight: theme.spacing(1) },
        statusBar: {
            paddingTop: (props: StyleProps) => props.statusBarHeight,
        },
        toolbar: {
            display: 'flex',
            justifyContent: 'space-between',
            paddingRight: 0,
            paddingLeft: 0,
            alignItems: 'center',
            flex: 1,
            minHeight: 52,
            backgroundColor: 'transparent',
        },
        tooltipDiv: {
            display: 'flex',
            paddingLeft: 20,
        },
        menuPadding: {
            position: 'absolute',
            top: 20,
            right: 20,
            zIndex: 4,
            background: theme.palette.colors.background.appBar,
            width: 54,
            height: 54,
            padding: 12,
            borderRadius: 100,
            color: theme.palette.colors.fixed.darkWhite,
            border: `1px solid ${theme.palette.colors.background.primaryElement}`,
            boxShadow: '0px 4px 8px 0px #2F313F1A',
            '&:hover': {
                backgroundColor: theme.palette.colors.background.appBar,
            },
        },
        statusBarTop: {
            top: (props: StyleProps) => `${props.statusBarHeight}px!important`,
        },
        leftBurger: {
            left: 30,
        },
        whiteLogo: {
            marginTop: 5,
            '& path': {
                fill: 'white',
            },
        },
        bleuLogo: {
            marginTop: 5,
            // The logo has no dark variant on the CDN (iconToCdn ignores darkMode for IconLogo),
            // so the blue asset it serves has to be recoloured here to read on the dark drawer.
            ...(theme.palette.mode === 'dark' && {
                '& path': {
                    fill: theme.palette.colors.fixed.darkWhite,
                },
            }),
        },
        paddingAppBar: {
            paddingRight: 20,
            paddingLeft: 30,
            // paddingLeft: 20,
            // paddingTop: 80,
            // background: theme.palette.secondary.light,
        },
        muiDrawer: {
            width: '80%',
            borderRadius: '0px 24px 24px 0px',
            backgroundColor: theme.palette.secondary.light,
            overflow: 'visible',
            // backgroundColor: theme.palette.background.default,
        },
        imgContainer: {
            position: 'relative',
            width: '100%' /* Adjust width as needed */,
            // height: '100%' /* Match the height of the drawer */,
        },
        img: {
            position: 'absolute',
            top: (props: StyleProps) => (props.isWebview ? -66 : 0),
            left: '100%',
            width: 40,
            objectFit: 'cover' /* Adjust as needed */,
        },
        imgNotOpen: {
            top: 66,
            left: 0,
            width: 40,
            position: 'absolute',
            zIndex: 2000,
        },
        noDecoration: {
            textDecoration: 'none',
        },
        versions: {
            fontSize: 14,
            paddingRight: 20,
            paddingLeft: 30,
            // Last row of the drawer, which runs to the bottom of an edge-to-edge WebView: the
            // system bar would sit on top of it.
            paddingBottom: (props: StyleProps) => 16 + props.bottomBarHeight,
            color: theme.palette.secondary.contrastText,
        },
    }),
);

export default MobileAppBar;
